import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Flex,
  SimpleGrid,
  Text,
  Button,
  Badge,
  Icon,
  Avatar,
  Select,
  Spinner,
  useColorModeValue,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Stack,
} from "@chakra-ui/react";
import { FaUserPlus, FaRss, FaHourglassHalf, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { statut as statutVeille } from "variables/veille";
import { supabase } from "lib/supabase";
import { useSession } from "contexts/SessionContext";
import { useVeilles } from "contexts/VeillesContext";
import ProfileHeader from "components/ProfileHeader";

const STATUT_DEMANDE = {
  en_attente: { label: "En attente", color: "orange", icon: FaHourglassHalf },
  acceptee: { label: "Acceptée", color: "green", icon: FaCheckCircle },
  refusee: { label: "Refusée", color: "red", icon: FaTimesCircle },
};

export default function MonEspace() {
  const textColor = useColorModeValue("gray.700", "white");
  const toast = useToast();
  const { org, profile } = useSession();
  const orgId = org && org.id;
  const { veilles, loading: veillesLoading } = useVeilles();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [membres, setMembres] = useState([]);
  const [demandes, setDemandes] = useState([]);
  const [accesVeille, setAccesVeille] = useState(new Set()); // user_ids ayant déjà accès à la veille choisie
  const [envoi, setEnvoi] = useState(false);

  const [veilleId, setVeilleId] = useState("");
  const [cibleId, setCibleId] = useState("");

  const chargerMembres = useCallback(async () => {
    if (!orgId) return;
    const { data } = await supabase
      .from("org_members")
      .select("user_id, role, statut, profiles(full_name, email, avatar_url)")
      .eq("organisation_id", orgId)
      .eq("statut", "actif");
    setMembres(data || []);
  }, [orgId]);

  const chargerDemandes = useCallback(async () => {
    if (!profile) return;
    const { data } = await supabase
      .from("veille_access_requests")
      .select("id, veille_id, target_user_id, statut, created_at")
      .eq("requested_by", profile.id)
      .order("created_at", { ascending: false });
    setDemandes(data || []);
  }, [profile]);

  useEffect(() => { chargerMembres(); chargerDemandes(); }, [chargerMembres, chargerDemandes]);

  const nom = (m) => (m.profiles && (m.profiles.full_name || m.profiles.email)) || m.user_id;
  const membreParId = (uid) => membres.find((m) => m.user_id === uid);
  const veilleParId = (id) => veilles.find((v) => v.id === id);

  async function ouvrirInvitation(vId) {
    setVeilleId(vId || "");
    setCibleId("");
    setAccesVeille(new Set());
    onOpen();
    if (vId) chargerAcces(vId);
  }

  async function chargerAcces(vId) {
    const { data } = await supabase.from("veille_access").select("user_id").eq("veille_id", vId);
    setAccesVeille(new Set((data || []).map((a) => a.user_id)));
  }

  // Collaborateurs éligibles : membres actifs, hors soi-même, hors ceux qui ont déjà accès à la veille
  const eligibles = membres.filter(
    (m) => profile && m.user_id !== profile.id && !accesVeille.has(m.user_id)
  );

  async function envoyerDemande() {
    if (!veilleId || !cibleId) {
      return toast({ title: "Choisissez une veille et un collaborateur.", status: "warning", duration: 2500 });
    }
    setEnvoi(true);
    const { data, error } = await supabase.rpc("request_veille_access", { p_veille: veilleId, p_target: cibleId });
    if (error) {
      setEnvoi(false);
      return toast({ title: "Demande impossible", description: error.message, status: "error", duration: 3500 });
    }
    // Notification e-mail à l'administrateur (best-effort : la demande est enregistrée quoi qu'il arrive)
    try {
      await supabase.functions.invoke("notify-access-request", { body: { request_id: data } });
    } catch (e) { /* la fonction e-mail sera activée côté Supabase */ }
    setEnvoi(false);
    onClose();
    toast({
      title: "Demande envoyée",
      description: "Votre administrateur va la recevoir par e-mail et décider.",
      status: "success",
      duration: 3500,
    });
    chargerDemandes();
  }

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      {/* En-tête */}
      <Flex align="center" mb="28px">
        <Box w="7px" h="60px" borderRadius="full" bg="teal.300" me="18px" flexShrink={0} />
        <Box>
          <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" color={textColor} lineHeight="1.1">Mon espace</Text>
          <Text fontSize="md" color="gray.700" mt="4px">Votre profil et les veilles sur lesquelles vous travaillez.</Text>
        </Box>
      </Flex>

      {/* Profil */}
      <ProfileHeader
        stats={[
          { value: veilles.length, label: "veilles" },
          { value: demandes.filter((d) => d.statut === "en_attente").length, label: "demandes" },
        ]}
      />

      {/* Mes veilles */}
      <Box bg="gray.100" borderRadius="20px" p={{ base: "16px", md: "22px" }} mb="24px">
        <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} direction={{ base: "column", md: "row" }} gap="12px" mb="18px" px="4px">
          <Flex align="center">
            <Box w="6px" h="42px" borderRadius="full" bg="teal.300" me="14px" flexShrink={0} />
            <Box>
              <Text fontSize="lg" fontWeight="bold" color={textColor}>Mes veilles</Text>
              <Text fontSize="sm" color="gray.700">Les veilles sur lesquelles l'administrateur vous a donné accès.</Text>
            </Box>
          </Flex>
          <Button bg="teal.300" color="white" _hover={{ bg: "teal.400" }} borderRadius="12px" leftIcon={<Icon as={FaUserPlus} />} onClick={() => ouvrirInvitation("")}>Inviter un collaborateur</Button>
        </Flex>

        {veillesLoading ? (
          <Flex justify="center" py="30px"><Spinner color="teal.300" /></Flex>
        ) : veilles.length === 0 ? (
          <Box bg="white" borderRadius="16px" p="24px"><Text fontSize="sm" color="gray.500">Aucune veille ne vous est attribuée pour l'instant. Votre administrateur peut vous en donner accès.</Text></Box>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing="16px">
            {veilles.map((v) => {
              const s = statutVeille(v.statut);
              return (
                <Box key={v.id} bg="white" borderRadius="16px" p="20px" boxShadow="0 2px 6px rgba(20,20,43,0.04)">
                  <Flex justify="space-between" align="flex-start" mb="10px">
                    <Text fontSize="md" fontWeight="bold" color="gray.800" noOfLines={2}>{v.nom}</Text>
                    <Badge colorScheme={s.color} borderRadius="8px" flexShrink={0} ml="8px">{s.label}</Badge>
                  </Flex>
                  <Text fontSize="sm" color="gray.500" mb="16px">{v.client}</Text>
                  <Button size="sm" variant="outline" colorScheme="teal" borderRadius="10px" w="100%" leftIcon={<Icon as={FaUserPlus} fontSize="12px" />} onClick={() => ouvrirInvitation(v.id)}>
                    Inviter un collaborateur
                  </Button>
                </Box>
              );
            })}
          </SimpleGrid>
        )}
      </Box>

      {/* Mes demandes */}
      {demandes.length > 0 && (
        <Box bg="gray.100" borderRadius="20px" p={{ base: "16px", md: "22px" }}>
          <Flex align="center" mb="16px" px="4px">
            <Box w="6px" h="42px" borderRadius="full" bg="teal.300" me="14px" flexShrink={0} />
            <Box>
              <Text fontSize="lg" fontWeight="bold" color={textColor}>Mes demandes d'invitation</Text>
              <Text fontSize="sm" color="gray.700">Statut des accès que vous avez demandés à l'administrateur.</Text>
            </Box>
          </Flex>
          <Flex direction="column" gap="10px">
            {demandes.map((d) => {
              const st = STATUT_DEMANDE[d.statut] || STATUT_DEMANDE.en_attente;
              const cible = membreParId(d.target_user_id);
              const v = veilleParId(d.veille_id);
              return (
                <Flex key={d.id} align="center" bg="white" borderRadius="12px" px="16px" py="12px" gap="12px" boxShadow="0 2px 6px rgba(20,20,43,0.04)">
                  <Avatar src={cible && cible.profiles && cible.profiles.avatar_url} name={cible ? nom(cible) : ""} size="sm" />
                  <Box flex="1" minW="0">
                    <Text fontSize="sm" fontWeight="bold" color="gray.800" isTruncated>{cible ? nom(cible) : "Collaborateur"}</Text>
                    <Text fontSize="xs" color="gray.500" isTruncated>Veille : {v ? (v.client || v.nom) : "—"}</Text>
                  </Box>
                  <Badge colorScheme={st.color} borderRadius="8px"><Flex align="center"><Icon as={st.icon} me="5px" fontSize="10px" />{st.label}</Flex></Badge>
                </Flex>
              );
            })}
          </Flex>
        </Box>
      )}

      {/* Modal : demander un accès pour un collaborateur */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
        <ModalOverlay />
        <ModalContent borderRadius="15px">
          <ModalHeader>Inviter un collaborateur sur une veille</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing="14px">
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold">Veille</FormLabel>
                <Select placeholder="Choisir une veille" value={veilleId} onChange={(e) => { setVeilleId(e.target.value); setCibleId(""); if (e.target.value) chargerAcces(e.target.value); }} borderRadius="12px" fontSize="sm">
                  {veilles.map((v) => (<option key={v.id} value={v.id}>{v.client || v.nom}</option>))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold">Collaborateur</FormLabel>
                <Select placeholder="Choisir un collaborateur" value={cibleId} onChange={(e) => setCibleId(e.target.value)} borderRadius="12px" fontSize="sm" isDisabled={!veilleId}>
                  {eligibles.map((m) => (<option key={m.user_id} value={m.user_id}>{nom(m)}</option>))}
                </Select>
                {veilleId && eligibles.length === 0 && (
                  <Text fontSize="xs" color="gray.500" mt="6px">Tous vos collaborateurs ont déjà accès à cette veille.</Text>
                )}
              </FormControl>
              <Box bg="gray.50" borderRadius="12px" p="12px">
                <Text fontSize="xs" color="gray.600">Votre demande est envoyée à l'administrateur par e-mail. S'il l'accepte, l'accès à la veille est donné automatiquement au collaborateur.</Text>
              </Box>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" borderRadius="12px" mr="10px" onClick={onClose}>Annuler</Button>
            <Button bg="teal.300" color="white" _hover={{ bg: "teal.400" }} borderRadius="12px" leftIcon={<Icon as={FaUserPlus} />} onClick={envoyerDemande} isLoading={envoi}>Envoyer la demande</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
